import { DeleteOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  List,
  Popconfirm,
  Radio,
  Space,
  Switch,
  Typography,
} from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useProductsAddAlias,
  useProductsDeleteAlias,
  useProductsGetProduct,
  useProductsUpdateProduct,
} from '@/api/generated/endpoints/products/products';
import type { AliasCreate, ProductDetail, ProductUpdate } from '@/api/generated/model';
import { useHasRole } from '@/auth/useHasRole';
import { PRODUCT_FIELD_BY_CODE } from '@/pages/catalog/productForm';
import { problemMessage } from '@/i18n/problem';
import { applyProblem, problemText } from '@/lib/problemForm';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';

/** Карточка товара: реквизиты (правит менеджер) и алиасы — как товар называется в УПД. */
export function ProductDrawer({
  productId,
  onClose,
}: {
  productId: number | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const detail = useProductsGetProduct(productId ?? 0, { query: { enabled: productId !== null } });
  const p = detail.data;
  return (
    <Drawer
      title={p?.name ?? t('products.drawerTitle')}
      open={productId !== null}
      onClose={onClose}
      width={mobile ? '100%' : 520}
      loading={detail.isPending && productId !== null}
      destroyOnHidden
    >
      {p && <ProductEditor product={p} />}
    </Drawer>
  );
}

function ProductEditor({ product }: { product: ProductDetail }) {
  const { t } = useTranslation();
  const canEdit = useHasRole('manager');
  const canAddAlias = useHasRole('storekeeper');
  const [form] = Form.useForm<ProductUpdate>();
  const [aliasForm] = Form.useForm<AliasCreate>();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const refresh = () => invalidatePath(queryClient, '/api/v1/products');

  useEffect(() => {
    form.setFieldsValue({
      name: product.name,
      base_uom: product.base_uom,
      shelf_life_days: product.shelf_life_days,
      sku: product.sku,
      is_active: product.is_active,
    });
  }, [form, product]);

  const update = useProductsUpdateProduct({
    mutation: {
      onSuccess: async () => {
        await refresh();
        void message.success(t('common.saved'));
      },
      onError: (e) => {
        if (!applyProblem(form, e, PRODUCT_FIELD_BY_CODE)) void message.error(problemMessage(e));
      },
    },
  });
  const addAlias = useProductsAddAlias({
    mutation: {
      onSuccess: async () => {
        aliasForm.resetFields();
        await refresh();
      },
      onError: (e) => {
        if (!applyProblem(aliasForm, e, { alias_conflict: 'alias' })) {
          void message.error(problemMessage(e));
        }
      },
    },
  });
  const deleteAlias = useProductsDeleteAlias({
    mutation: {
      onSuccess: refresh,
      onError: (e) => void message.error(problemText(e)),
    },
  });

  return (
    <>
      <Form<ProductUpdate>
        form={form}
        layout="vertical"
        disabled={!canEdit}
        onFinish={(values) => {
          update.mutate({
            productId: product.id,
            data: { ...values, sku: values.sku?.trim() ? values.sku : null },
          });
        }}
      >
        <Form.Item
          name="name"
          label={t('products.name')}
          rules={[{ required: true, whitespace: true, message: t('products.nameRequired') }]}
        >
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item
          name="base_uom"
          label={t('products.baseUom')}
          extra={t('products.baseUomLocked')}
        >
          <Radio.Group
            optionType="button"
            options={[
              { label: t('uom.kgLong'), value: 'kg' },
              { label: t('uom.pcsLong'), value: 'pcs' },
              { label: t('uom.boxLong'), value: 'box' },
            ]}
          />
        </Form.Item>
        <Space size="large">
          <Form.Item name="shelf_life_days" label={t('products.shelfLife')}>
            <InputNumber min={1} max={3650} precision={0} />
          </Form.Item>
          <Form.Item name="sku" label={t('products.sku')}>
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item name="is_active" label={t('common.active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
        {canEdit && (
          <Button type="primary" htmlType="submit" loading={update.isPending}>
            {t('common.save')}
          </Button>
        )}
      </Form>

      <Divider orientation="left">{t('products.aliasesTitle')}</Divider>
      <Typography.Paragraph type="secondary">{t('products.aliasesHint')}</Typography.Paragraph>
      <List
        size="small"
        bordered
        locale={{ emptyText: t('products.aliasesEmpty') }}
        dataSource={product.aliases}
        renderItem={(a) => (
          <List.Item
            actions={
              canEdit
                ? [
                    <Popconfirm
                      key="delete"
                      title={t('products.aliasDeleteConfirm')}
                      onConfirm={() => {
                        deleteAlias.mutate({ productId: product.id, aliasId: a.id });
                      }}
                    >
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]
                : []
            }
          >
            {a.alias}
          </List.Item>
        )}
      />
      {canAddAlias && (
        <Form<AliasCreate>
          form={aliasForm}
          layout="inline"
          style={{ marginTop: 12 }}
          onFinish={(v) => {
            addAlias.mutate({ productId: product.id, data: v });
          }}
        >
          <Form.Item
            name="alias"
            style={{ flex: 1 }}
            rules={[{ required: true, whitespace: true, message: t('products.nameRequired') }]}
          >
            <Input placeholder={t('products.aliasPlaceholder')} maxLength={300} />
          </Form.Item>
          <Button htmlType="submit" loading={addAlias.isPending}>
            {t('common.add')}
          </Button>
        </Form>
      )}
    </>
  );
}
